import { Injectable, NgZone } from '@angular/core';
import {
  NbAuthResult,
  NbAuthSimpleToken,
  NbAuthStrategy,
  NbAuthStrategyClass,
  NbAuthStrategyOptions,
} from '@nebular/auth';
import { from, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { APIService } from '../api';

export interface GoogleSigninStrategyOptions extends NbAuthStrategyOptions {
  clientId: string;
  redirect?: {
    success?: string,
  };
}

/**
 * Strategy that spawns a Google OAuth2 process and then call our backend API
 * to retrieve a user token.
 */
@Injectable()
export class GoogleSigninStrategy extends NbAuthStrategy {

  protected defaultOptions: GoogleSigninStrategyOptions = {
    name: 'google-signin',
    clientId: null,
    token: {
      name: 'token',
      class: NbAuthSimpleToken,
    },
    redirect: {
      success: '/',
    },
  };

  constructor(
    private api: APIService,
    private ngZone: NgZone,
  ) {
    super();
  }

  static setup(options: GoogleSigninStrategyOptions): [NbAuthStrategyClass, GoogleSigninStrategyOptions] {
    return [GoogleSigninStrategy, options];
  }

  authenticate(): Observable<NbAuthResult> {
    return new Observable((observer) => {
      gapi.load('auth2', () => {
        gapi.auth2.init({
          client_id: this.getOption('clientId'),
        }).then((auth2) => {
          // Spawns Google popup
          from(auth2.signIn({
            ux_mode: 'popup',
          })).pipe(
            // Call our API with the access token
            switchMap((googleUser) => {
              const authResponse = googleUser.getAuthResponse(true);
              return this.api.users_google_login({
                idToken: authResponse.id_token,
                accessToken: authResponse.access_token,
                platform: 'default',
                email: googleUser.getBasicProfile().getEmail(),
              });
            }),
          ).subscribe({
            // Return our token
            next: (result) => {
              // NgZone is needed because we are in the gapi callback
              this.ngZone.run(() => {
                observer.next(new NbAuthResult(
                  true,
                  {},
                  this.getOption('redirect.success'),
                  null,
                  null,
                  this.createToken<NbAuthSimpleToken>(result.token),
                ));
                observer.complete();
              });
            },
            // Handle error
            error: (err) => {
              // NgZone is needed because we are in the gapi callback
              this.ngZone.run(() => {
                const errorMessage = err.error;
                observer.next(new NbAuthResult(
                  false,
                  {},
                  null,
                  // Ignore popup closed error
                  errorMessage !== 'popup_closed_by_user' ? errorMessage : null,
                  null,
                  null,
                ));
                observer.complete();
              });
            },
          });
        });
      });
    });
  }

  register(_data?: any): Observable<NbAuthResult> {
    throw new Error('`register` is not supported by `TokenStrategy`.');
  }

  requestPassword(_data?: any): Observable<NbAuthResult> {
    throw new Error('`requestPassword` is not supported by `TokenStrategy`.');
  }

  resetPassword(_data?: any): Observable<NbAuthResult> {
    throw new Error('`resetPassword` is not supported by `TokenStrategy`.');
  }

  logout(): Observable<NbAuthResult> {
    return of(new NbAuthResult(true));
  }

  refreshToken(_data?: any): Observable<NbAuthResult> {
    throw new Error('`refreshToken` is not supported by `TokenStrategy`.');
  }

}
